import React from 'react';
import { Text, TagBadge } from '@chatsdet/ui';
import type { TagColor } from '@chatsdet/ui';
import { useCodeFlowRevisionSource, useCodeFlowRunSteps } from './useCodeFlowRun';
import type { CodeFlowRunStepDto, CodeFlowStepMode, CodeFlowStepOutcome } from './types';

const OUTCOME_COLOR: Record<CodeFlowStepOutcome, TagColor> = {
  passed: 'green',
  failed: 'red',
  warned: 'yellow',
  skipped: 'gray',
};

const MODE_COLOR: Record<CodeFlowStepMode, TagColor> = {
  code: 'blue',
  agent: 'purple',
  'agent-by-design': 'purple',
  judge: 'teal',
};

/** Group steps by flowPath ("" is the top-level flow), then nodeKey, keeping first-seen order. */
const groupSteps = (steps: CodeFlowRunStepDto[]) => {
  const flows = new Map<string, Map<string, CodeFlowRunStepDto[]>>();
  for (const step of steps) {
    const nodes = flows.get(step.flowPath) ?? new Map<string, CodeFlowRunStepDto[]>();
    const node = nodes.get(step.nodeKey) ?? [];
    node.push(step);
    nodes.set(step.nodeKey, node);
    flows.set(step.flowPath, nodes);
  }
  return flows;
};

const StepRow: React.FC<{ step: CodeFlowRunStepDto }> = ({ step }) => (
  <li className="flex flex-col gap-1 py-1.5">
    <div className="flex flex-wrap items-center gap-2">
      <TagBadge name={step.mode} color={MODE_COLOR[step.mode]} size="sm" hideDot />
      <TagBadge name={step.outcome} color={OUTCOME_COLOR[step.outcome]} size="sm" />
      <Text size="sm" className="min-w-0 break-words">
        {step.stepLabel}
      </Text>
    </div>
    {step.error && (
      <Text size="xs" className="text-destructive font-mono break-words whitespace-pre-wrap">
        {step.error}
      </Text>
    )}
  </li>
);

const RevisionSource: React.FC<{ workflowId: string; rev: number }> = ({ workflowId, rev }) => {
  const source = useCodeFlowRevisionSource(workflowId, rev);
  return (
    <details className="mt-3 border-t border-border pt-3">
      <summary className="cursor-pointer text-sm font-medium">Source (revision {rev})</summary>
      {source.data ? (
        <pre className="mt-2 max-h-96 overflow-auto rounded-md bg-muted/30 p-2 font-mono text-xs">
          {source.data.sourceTs}
        </pre>
      ) : (
        <Text size="xs" className="text-muted-foreground mt-2">
          {source.isError ? 'Could not load the source.' : 'Loading...'}
        </Text>
      )}
    </details>
  );
};

/**
 * Step list for a code-flow run, shown in place of the node list. Renders `children`
 * (the node list) for every other run, and while the probe is in flight.
 */
export const CodeFlowRunSteps: React.FC<{ runId: string; children: React.ReactNode }> = ({ runId, children }) => {
  const { data, isError } = useCodeFlowRunSteps(runId);
  if (isError) {
    return (
      <Text size="sm" className="text-destructive">
        Could not load the code-flow steps for this run.
      </Text>
    );
  }
  if (!data) return <>{children}</>;

  const flows = groupSteps(data.steps);
  return (
    <div className="flex flex-col gap-3">
      <Text size="xs" className="text-muted-foreground">
        Revision {data.run.rev} · {data.run.mode} mode
      </Text>
      {data.run.error && (
        <Text size="xs" className="text-destructive font-mono break-words whitespace-pre-wrap">
          {data.run.error}
        </Text>
      )}
      {data.steps.length === 0 && (
        <Text size="sm" className="text-muted-foreground">
          No steps recorded yet.
        </Text>
      )}
      {[...flows].map(([flowPath, nodes]) => (
        <section key={flowPath} className="flex flex-col gap-2">
          {flowPath !== '' && <Text size="xs" className="font-mono text-muted-foreground">{flowPath}</Text>}
          {[...nodes].map(([nodeKey, steps]) => (
            <div key={nodeKey}>
              <Text size="sm" className="font-medium">
                {nodeKey}
              </Text>
              <ul className="divide-y divide-border">
                {steps.map((step, i) => (
                  <StepRow key={i} step={step} />
                ))}
              </ul>
            </div>
          ))}
        </section>
      ))}
      <RevisionSource workflowId={data.run.workflowId} rev={data.run.rev} />
    </div>
  );
};
